import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Loader2, AlertCircle, RefreshCw, Folder, Search, X, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useFileTree } from '@/hooks/useProjectCode'
import { FileTree } from './FileTree'

interface FileTreePanelProps {
  projectId: number
  selectedPath: string | null
  onFileSelect: (path: string) => void
  onInsertPrompt?: (prompt: string) => void
}

export function FileTreePanel({ projectId, selectedPath, onFileSelect, onInsertPrompt }: FileTreePanelProps) {
  const { t } = useTranslation('conversations')
  const { data, isLoading, error, refetch, isRefetching } = useFileTree(projectId)
  const [searchQuery, setSearchQuery] = useState('')

  const treeData = data?.tree || []

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <span className="text-sm">{t('code.loading')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <span className="text-sm text-destructive mb-3">{t('code.loadError')}</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          {t('code.retry')}
        </Button>
      </div>
    )
  }

  if (!treeData.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center text-muted-foreground">
        <Folder className="h-8 w-8 mb-2 opacity-50" />
        <span className="text-sm">{t('code.emptyTree')}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header with refresh */}
      <div className="flex items-center justify-between px-3 h-10 border-b bg-muted/30 flex-shrink-0">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t('code.files')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => refetch()}
          disabled={isRefetching}
          title={t('code.refreshTree')}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
        </Button>
      </div>

      {/* Search input */}
      <div className="px-2 py-2 border-b flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t('code.searchFiles')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 text-xs pl-7 pr-7"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Tree - uses native CSS scrolling */}
      <div className="flex-1 min-h-0 overflow-y-auto dark:bg-[#0d1117] bg-white">
        <FileTree
          data={treeData}
          selectedPath={selectedPath}
          onSelect={onFileSelect}
          searchQuery={searchQuery}
        />
      </div>

      {/* Visualize button - fixed at bottom */}
      {onInsertPrompt && (
        <div className="border-t px-2 py-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onInsertPrompt(t('code.visualizePrompt', 'Draw an Excalidraw diagram of the codebase architecture'))}
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            {t('code.visualizeButton', 'Visualize')}
          </Button>
        </div>
      )}
    </div>
  )
}
